import { Alert, alpha } from '@mui/material';
import styled from 'styled-components';

import { PUBLIC_HEADER_OFFSET } from 'shared-components/public-shell';
import { SYNMATCH_COLORS, SYNMATCH_RADII, SYNMATCH_SHADOWS } from 'theme';

export const PublicHero = styled.div`
  border-bottom: 0.0625rem solid ${SYNMATCH_COLORS.border.default};
  padding-block: 4rem 2.75rem;

  @media (max-width: 48rem) {
    padding-block: 3.25rem 2.25rem;
  }

  @media (max-width: 30rem) {
    padding-block: 2.5rem 1.75rem;
  }
`;

export const HeroInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;

export const HeroBackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  font-size: 0.875rem;
  font-weight: 480;
  text-decoration: none;
  color: ${SYNMATCH_COLORS.text.muted};
  border-radius: ${SYNMATCH_RADII.sm};
  transition: color 150ms ease, gap 150ms ease;

  &:hover {
    color: ${SYNMATCH_COLORS.primary};
    gap: 0.7rem;
  }

  &:focus-visible {
    outline: 0.125rem solid ${SYNMATCH_COLORS.primary};
    outline-offset: 0.25rem;
  }

  .MuiSvgIcon-root {
    font-size: 1rem;
  }
`;

export const HeroEyebrow = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: -0.004em;
  color: ${SYNMATCH_COLORS.text.muted};
`;

export const HeroTitle = styled.h1`
  margin: 0;
  max-width: 22ch;
  font-size: clamp(1.875rem, 6vw, 3rem);
  font-weight: 640;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: ${SYNMATCH_COLORS.primary};
  overflow-wrap: anywhere;
`;

export const HeroIntroText = styled.p`
  margin: 0.25rem 0 0;
  max-width: 46rem;
  font-size: 1.125rem;
  line-height: 1.55;
  letter-spacing: -0.01em;
  color: ${SYNMATCH_COLORS.text.muted};
  white-space: pre-line;

  @media (max-width: 30rem) {
    font-size: 1.0625rem;
  }
`;

export const PublicContentLayout = styled.div`
  padding: 3rem 0 4rem;
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr);
  gap: 3.5rem;
  align-items: start;

  @media (max-width: 48rem) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const JobContentColumn = styled.article`
  min-width: 0;
  max-width: 42rem;
`;

export const PublicJobMarkdownBody = styled.div`
  font-size: 1rem;
  line-height: 1.65;
  color: ${SYNMATCH_COLORS.text.default};
  overflow-wrap: anywhere;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 1.125rem;
    font-weight: 560;
    letter-spacing: -0.014em;
    color: ${SYNMATCH_COLORS.primary};
    margin: 1.75rem 0 0.5rem;
  }

  p {
    margin: 0 0 1rem;
  }

  ul,
  ol {
    margin: 0 0 1.25rem;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    margin-bottom: 0.6rem;
    padding-left: 1.5rem;
  }

  li::before {
    content: '';
    position: absolute;
    left: 0.0625rem;
    top: 0.65em;
    width: 0.5rem;
    height: 0.0625rem;
    background: ${SYNMATCH_COLORS.text.muted};
  }

  ol {
    counter-reset: jd-ordered;
  }

  ol > li {
    counter-increment: jd-ordered;
  }

  ol > li::before {
    content: counter(jd-ordered) '.';
    top: 0;
    width: auto;
    height: auto;
    background: none;
    font-weight: 560;
    color: ${SYNMATCH_COLORS.text.muted};
  }

  strong {
    font-weight: 580;
    color: ${SYNMATCH_COLORS.primary};
  }

  > :first-child {
    margin-top: 0;
  }
`;

export const JobSummaryCard = styled.aside`
  border: 0.0625rem solid ${SYNMATCH_COLORS.border.default};
  border-radius: ${SYNMATCH_RADII.lg};
  background: ${SYNMATCH_COLORS.surface.default};
  box-shadow: ${SYNMATCH_SHADOWS.card};
  padding: 1.75rem;

  @media (min-width: 48.0625rem) {
    position: sticky;
    top: ${PUBLIC_HEADER_OFFSET};
  }

  .MuiButtonBase-root {
    margin-top: 1.5rem;
  }
`;

export const SummaryList = styled.dl`
  margin: 0;
  display: flex;
  flex-direction: column;
`;

export const SummaryItem = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-top: 0.0625rem solid ${SYNMATCH_COLORS.border.default};

  &:first-child {
    padding-top: 0;
    border-top: 0;
  }
`;

export const SummaryTerm = styled.dt`
  font-size: 0.85rem;
  color: ${SYNMATCH_COLORS.text.muted};
`;

export const SummaryDetail = styled.dd`
  margin: 0;
  min-width: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.008em;
  text-align: right;
  color: ${SYNMATCH_COLORS.text.default};
  overflow-wrap: anywhere;
`;

export const PublicStateContainer = styled.div`
  max-width: 40rem;
  margin: 0 auto;
  padding: 4rem 0;
`;

export const SkeletonJobBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

export const SkeletonSummaryList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SkeletonSummaryRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-top: 0.0625rem solid ${SYNMATCH_COLORS.border.default};

  &:first-child {
    padding-top: 0;
    border-top: 0;
  }
`;

export const ClosedApplicationsNotice = styled(Alert)`
  && {
    align-self: stretch;
    align-items: flex-start;
    margin-top: 0.875rem;
    padding: 1.5rem;
    border-radius: ${SYNMATCH_RADII.lg};
    background: ${alpha(SYNMATCH_COLORS.status.info.bg, 0.65)};
    border-color: ${alpha(SYNMATCH_COLORS.primary, 0.14)};
  }

  &&.MuiAlert-standardWarning {
    background: ${alpha(SYNMATCH_COLORS.status.warn.bg, 0.65)};
    border-color: ${alpha(SYNMATCH_COLORS.status.warn.fg, 0.2)};
  }

  .MuiAlert-icon {
    margin-right: 1rem;
    padding: 0.125rem 0 0;
  }

  .MuiAlert-message {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    width: 100%;
    min-width: 0;
    padding: 0;
  }

  .MuiButton-root {
    flex-shrink: 0;
  }

  @media (max-width: 48rem) {
    && {
      padding: 1.25rem;
    }
    .MuiAlert-message {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }
  }
`;

export const ClosedApplicationsHeading = styled.h2`
  margin: 0 0 0.4375rem;
  font-size: 1.125rem;
  font-weight: 620;
  line-height: 1.35;
  letter-spacing: -0.015em;

  &:focus {
    outline: none;
  }
`;

export const ClosedApplicationsText = styled.p`
  margin: 0;
  color: ${SYNMATCH_COLORS.text.muted};
  font-size: 0.9375rem;
  line-height: 1.65;
`;
